<template>
  <div :id="`v-form-builder-question-${question.id}`" class="v-form-builder-question" :class="[`v-form-builder-question--${question.type}`, { 'is-internal': question.internal }]">
    <div class="v-form-builder-question__header">
      <div class="flex-center" style="justify-content: space-between">
        <div class="v-form-builder-question__header-left flex-center">
          <VSelect :model-value="question.type" size="medium" style="width: 200px" @update:model-value="updateQuestionType">
            <VOption v-for="(label, value) in selectableQuestionTypeOptions" :key="value" :label :value />
          </VSelect>

          <VButton class="v-form-builder-question__delete" icon="Delete" icon-type="info" rounded type="icon" @click="blueprint.removeQuestion(question)" />
          <VButton class="v-form-builder-question__copy" icon="Copy" icon-type="info" rounded type="icon" @click="blueprint.copyQuestion({ question, index: index + 1 })" />
        </div>

        <div class="v-form-builder-question__header-right flex-center">
          <slot name="question-header-right" :question />
          <div class="flex-center">
            <VButton :disabled="isLastQuestion" icon="ChevronDown" rounded style="margin-right: 10px" type="white" @click="move(1)" />
            <VButton :disabled="isFirstQuestion" icon="ChevronUp" rounded type="white" @click="move(-1)" />
          </div>
        </div>
      </div>

      <div class="v-form-builder-question__header-bottom">
        <VSwitch v-if="question.type === 'true_false'" :model-value="question.options.length === 3" @update:model-value="toggleThirdOption">
          {{ i18n.allowThirdOption }}
        </VSwitch>

        <VSwitch v-if="question.allow_multiple_answers !== undefined" v-model="question.allow_multiple_answers">{{ i18n.allowMultipleAnswers }}</VSwitch>
        <VSwitch v-if="question.type !== 'repeater'" v-model="question.optional">{{ i18n.optional }}</VSwitch>
        <VSwitch v-if="blueprint.settings.showInternalSwitch && internalSwitchVisible" v-model="question.internal" @update:model-value="onUpdateInternal($event, question)">
          {{ i18n.internal }}
        </VSwitch>
        <slot name="question-header-bottom" :question />
      </div>
    </div>

    <div class="v-form-builder-question__body">
      <div class="v-form-builder-question__label">{{ i18n.question }}</div>
      <VInput v-model="question.title" :error="blueprint.errors[question.id]?.title" :placeholder="i18n.enterTheQuestion" size="medium" style="margin-bottom: 10px" />
      <template v-if="subtitleVisible">
        <div class="v-form-builder-question__label">{{ i18n.subtitle }}</div>
        <div class="flex-center" style="margin-bottom: 10px">
          <VInput v-model="question.subtitle" :placeholder="i18n.enterTheSubtitle" size="medium" style="margin-right: 5px" />
          <VButton icon="Close" :icon-size="20" rounded type="icon" @click="removeSubtitle" />
        </div>
      </template>
      <VButton v-else icon="Plus" :label="i18n.addSubtitle" size="small" style="margin-bottom: 20px" type="secondary" @click="subtitleVisible = true" />

      <template v-if="question.type === 'true_false'">
        <div class="v-form-builder-question__label">{{ i18n.answerOptions }}</div>

        <template v-for="option in question.options" :key="option.id">
          <div class="v-form-builder-question__option">
            <VRadio disabled label="" />
            <VInput v-model="option.label" :error="!option.label ? blueprint.errors[question.id]?.options : undefined" size="medium" />
            <slot name="true-false-option" :option :question />
          </div>

          <template v-if="!isNested">
            <TransitionGroup class="v-form-builder__questions" name="v-list" tag="div">
              <VFormBuilderQuestion
                v-for="childQuestion in (blueprint as TBlueprint).questions.filter((child) => child.option_id === option.id && child.parent_question_id === question.id)"
                :key="childQuestion.id"
                :blueprint
                is-nested
                :question-id="childQuestion.id"
              >
                <template #question-header-right="slotProps">
                  <slot name="question-header-right" v-bind="slotProps" />
                </template>
                <template #question-header-bottom="slotProps">
                  <slot name="question-header-bottom" v-bind="slotProps" />
                </template>
                <template #answer="slotProps">
                  <slot name="answer" v-bind="slotProps" />
                </template>
                <template #answer-label="slotProps">
                  <slot name="answer-label" v-bind="slotProps" />
                </template>
                <template #dropdown-option="slotProps">
                  <slot name="dropdown-option" v-bind="slotProps" />
                </template>
                <template #true-false-option="slotProps">
                  <slot name="true-false-option" v-bind="slotProps" />
                </template>
              </VFormBuilderQuestion>
            </TransitionGroup>

            <div class="v-form-builder-question__option v-form-builder-question__follow_up">
              <VFormBuilderSelectQuestion
                :blueprint="props.blueprint"
                :button-label="i18n.addFollowUpQuestion"
                button-type="secondary"
                @select="
                  props.blueprint.addQuestion($event, {
                    option_id: option.id,
                    parent_question_id: question.id,
                    internal: question.internal,
                    ...('is_template' in question ? { is_template: question.is_template } : {}),
                  })
                "
              />
            </div>
          </template>
        </template>
      </template>

      <VFormBuilderQuestionDropdown v-else-if="question.type === 'dropdown'" :blueprint :question>
        <template #dropdown-option="slotProps">
          <slot name="dropdown-option" v-bind="slotProps" />
        </template>
      </VFormBuilderQuestionDropdown>

      <template v-else-if="question.type === 'number'">
        <div class="v-form-builder-question__label">{{ i18n.answer }}</div>
        <VTag :plain="false" type="info">{{ i18n.userCanEnterANumber }}</VTag>
      </template>

      <template v-else-if="question.type === 'text'">
        <div class="v-form-builder-question__label">{{ i18n.answer }}</div>
        <VTag :plain="false" type="info">{{ i18n.userCanTypeTextInATextarea }}</VTag>
      </template>

      <template v-else-if="question.type === 'textarea'">
        <div class="v-form-builder-question__label">{{ i18n.answer }}</div>
        <VTag :plain="false" type="info">{{ i18n.userCanTypeLongTextInATextarea }}</VTag>
      </template>

      <template v-else-if="question.type === 'datepicker'">
        <div class="v-form-builder-question__label">{{ i18n.answer }}</div>
        <VTag :plain="false" type="info">{{ i18n.userCanPickADate }}</VTag>
      </template>

      <template v-else-if="question.type === 'upload'">
        <div class="v-form-builder-question__label">{{ i18n.answer }}</div>
        <VTag :plain="false" type="info">{{ i18n.userCanUploadOrDragFileHere }}</VTag>
      </template>

      <template v-else-if="question.type === 'url'">
        <div class="v-form-builder-question__label">{{ i18n.answer }}</div>
        <VTag :plain="false" type="info">{{ i18n.userCanEnterAUrl }}</VTag>
      </template>

      <VFormBuilderQuestionRepeater v-else-if="question.type === 'repeater'" :blueprint :question>
        <template #question-header-right="slotProps">
          <slot name="question-header-right" v-bind="slotProps" />
        </template>
        <template #question-header-bottom="slotProps">
          <slot name="question-header-bottom" v-bind="slotProps" />
        </template>
        <template #answer="slotProps">
          <slot name="answer" v-bind="slotProps" />
        </template>
        <template #answer-label="slotProps">
          <slot name="answer-label" v-bind="slotProps" />
        </template>
        <template #dropdown-option="slotProps">
          <slot name="dropdown-option" v-bind="slotProps" />
        </template>
        <template #true-false-option="slotProps">
          <slot name="true-false-option" v-bind="slotProps" />
        </template>
      </VFormBuilderQuestionRepeater>

      <template v-else-if="!props.blueprint.settings.question_types.includes(question.type)">
        <div class="v-form-builder-question__label">
          <slot name="answer-label" :question />
        </div>

        <slot name="answer" :question />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts" generic="TBlueprint extends Blueprint = Blueprint">
import { computed, onBeforeMount, ref } from 'vue'

import { i18nFormBuilder as i18n } from '../../utils/i18n.ts'
import VOption from '../Select/VOption.vue'
import VSelect from '../Select/VSelect.vue'
import VButton from '../VButton.vue'
import VInput from '../VInput.vue'
import VRadio from '../VRadio.vue'
import VSwitch from '../VSwitch.vue'
import VTag from '../VTag.vue'
import type { Blueprint, DropdownQuestion, Question, QuestionOption, QuestionType, TrueFalseQuestion } from './useBlueprint.ts'
import { useSelectableQuestionTypes } from './useSelectableQuestionTypes.ts'
import VFormBuilderQuestionDropdown from './VFormBuilderQuestionDropdown.vue'
import VFormBuilderQuestionRepeater from './VFormBuilderQuestionRepeater.vue'
import VFormBuilderSelectQuestion from './VFormBuilderSelectQuestion.vue'

interface Props {
  blueprint: TBlueprint
  questionId: number
  isNested?: boolean
}

const props = defineProps<Props>()

defineSlots<{
  'question-header-right'(props: { question: Question }): any
  'question-header-bottom'(props: { question: Question }): any
  answer(props: { question: Question }): any
  'answer-label'(props: { question: Question }): any
  'dropdown-option'(props: { question: DropdownQuestion; option: QuestionOption }): any
  'true-false-option'(props: { question: TrueFalseQuestion; option: QuestionOption }): any
}>()

const { selectableQuestionTypeOptions } = useSelectableQuestionTypes(props)

const subtitleVisible = ref(false)
const index = computed(() => props.blueprint.questions.findIndex((question) => question.id === props.questionId))
const visibleIndex = computed(() => props.blueprint.visibleQuestions.findIndex((question) => question.id === props.questionId))

const question = computed({
  get: () => props.blueprint.questions[index.value],
  set(newValue) {
    props.blueprint.questions[index.value] = newValue
  },
})

const isFirstQuestion = computed(() => {
  if (visibleIndex.value >= 0) {
    return visibleIndex.value === 0
  }

  const parentQuestionId = question.value.parent_question_id
  const optionId = question.value.option_id
  for (let i = index.value - 1; i > 0; i--) {
    const question = props.blueprint.questions[i]

    if (question.parent_question_id === parentQuestionId && question.option_id === optionId) {
      return false
    }
  }

  return true
})

const isLastQuestion = computed(() => {
  if (visibleIndex.value >= 0) {
    return visibleIndex.value === props.blueprint.visibleQuestions.length - 1
  }

  const parentQuestionId = question.value.parent_question_id
  const optionId = question.value.option_id

  const questionsToLoop =
    parentQuestionId && props.blueprint.questionsById[parentQuestionId].is_template === true
      ? props.blueprint.questions.filter((question) => question.is_template === true)
      : props.blueprint.questions

  for (let i = index.value + 1; i < questionsToLoop.length; i++) {
    const question = questionsToLoop[i]

    if (question.parent_question_id === parentQuestionId && question.option_id === optionId) {
      return false
    }
  }

  return true
})

const internalSwitchVisible = computed(() => !hasInternalParent(question.value))

onBeforeMount(() => (subtitleVisible.value = !!question.value.subtitle))

/**
 * Moves the question up or down in the list.
 */
function move(position: number) {
  const questions = props.blueprint.questions
  const visibleQuestions = props.blueprint.visibleQuestions

  // Handles non-nested questions.
  if (visibleIndex.value >= 0) {
    if (position > 0) {
      const nextQuestion = visibleQuestions[visibleIndex.value + 1]
      const nextQuestionIndex = questions.findIndex((question) => question.id === nextQuestion.id)
      questions.splice(nextQuestionIndex, 0, questions.splice(index.value, 1)[0])
    } else {
      const previousQuestion = visibleQuestions[visibleIndex.value - 1]
      const previousQuestionIndex = questions.findIndex((question) => question.id === previousQuestion.id)
      questions.splice(previousQuestionIndex, 0, questions.splice(index.value, 1)[0])
    }

    return
  }

  // Handles nested questions.
  const parentQuestionId = question.value.parent_question_id
  const optionId = question.value.option_id

  if (position > 0) {
    for (let i = index.value + 1; i < questions.length; i++) {
      const question = questions[i]

      if (question.parent_question_id === parentQuestionId && question.option_id === optionId) {
        questions.splice(i, 0, questions.splice(index.value, 1)[0])

        return
      }
    }
  } else {
    for (let i = index.value - 1; i > 0; i--) {
      const question = questions[i]

      if (question.parent_question_id === parentQuestionId && question.option_id === optionId) {
        questions.splice(i, 0, questions.splice(index.value, 1)[0])

        return
      }
    }
  }
}

async function updateQuestionType(type: QuestionType) {
  const properties: Record<string, unknown> = {
    internal: question.value.internal,
    optional: question.value.optional,
  }

  if (question.value.parent_question_id) {
    properties.parent_question_id = question.value.parent_question_id

    const parent = props.blueprint.questionsById[question.value.parent_question_id]
    // Only if the parent question is also a template question, should this question be a template question.
    if (question.value.is_template && parent.is_template) {
      properties.is_template = true
    }
  }

  if (question.value.option_id) {
    properties.option_id = question.value.option_id
  }

  const newIndex = index.value
  await props.blueprint.removeQuestion(question.value)
  props.blueprint.questions.splice(newIndex, 0, props.blueprint.createQuestion(type, properties))
}

function toggleThirdOption() {
  if (question.value.type !== 'true_false') {
    return
  }

  if (question.value.options.length === 3) {
    question.value.options.splice(2, 1)
  } else {
    question.value.options.push(props.blueprint.createOption(''))
  }
}

function removeSubtitle() {
  question.value.subtitle = ''
  subtitleVisible.value = false
}

function onUpdateInternal(internal: boolean, question: Question) {
  for (const blueprintQuestion of props.blueprint.questions) {
    if (blueprintQuestion.id === question.id) {
      continue
    }

    if (isParentOf(blueprintQuestion, question)) {
      blueprintQuestion.internal = internal
    }
  }
}

function isParentOf(child: Question, parent: Question) {
  if (!child.parent_question_id) {
    return false
  }

  if (child.parent_question_id === parent.id) {
    return true
  }

  const childParent = props.blueprint.questionsById[child.parent_question_id]

  if (!childParent) {
    return false
  }

  return isParentOf(childParent, parent)
}

function hasInternalParent(question: Question): boolean {
  if (!question.parent_question_id) {
    return false
  }

  const parent = props.blueprint.questionsById[question.parent_question_id]

  if (!parent) {
    return false
  }

  return parent.internal || hasInternalParent(parent)
}
</script>

<style>
.v-form-builder-question {
  background: var(--color-background);
  border: var(--border);
  border-radius: 10px;
  margin-bottom: 30px;
  transition: border-color var(--transition);

  /* Applies only to nested questions. */
  .v-form-builder__questions {
    margin-left: 30px;

    .v-form-builder-question {
      margin-bottom: 10px;
    }
  }
}

.v-form-builder-question__header {
  padding: 20px;
  background-color: var(--color-grey-6);
  border-radius: 10px 10px 0 0;
  color: var(--color-info);
  transition:
    background-color var(--transition),
    color var(--transition);

  .v-input {
    color: initial;
  }
}

.v-form-builder-question__header-left {
  gap: 5px;

  .v-form-builder-question__delete {
    margin-right: 0;
  }
}

.v-form-builder-question__header-right {
  gap: 30px;
}

.v-form-builder-question__header-bottom {
  display: flex;
  margin-top: 20px;
  gap: 20px;
}

.v-form-builder-question__body {
  padding: 20px;
  border-radius: 0 0 10px 10px;
  transition: background-color var(--transition);
}

.v-form-builder-question__label {
  font-weight: 500;
  margin-bottom: 10px;
}

.v-form-builder-question__option {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  gap: 5px;
  transition: transform var(--transition);

  .v-radio {
    margin-right: 10px;
  }

  .v-input {
    max-width: 300px;
  }

  &.is-dragging {
    transform: translateX(10px);
  }
}

.v-form-builder-question__follow_up {
  margin-left: 25px;
}

.v-form-builder-question__option-grabber {
  margin-left: 10px;
}

.v-form-builder-question.is-internal {
  border-color: var(--color-primary);

  .v-form-builder-question__header {
    background-color: var(--color-blue-5);
  }

  .v-form-builder-question__header-right {
    color: var(--color-primary);
  }

  .v-form-builder-question__body {
    background-color: var(--color-blue-1);
  }
}
</style>
